import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMatch, useLocation } from 'react-router-dom';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { isEmbedReadyMessage, postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { LogoConfig } from 'src/components/context/AppContextModels';
import { ROUTE_CONFIG } from 'src/utilities/RouteConfig';

export interface EmbeddedAppInfo {
  url: string | null;
  name: string | null;
  logoConfig: LogoConfig | null;
  productType: string | null;
}

export interface EmbeddedAppState {
  embeddedApp: EmbeddedAppInfo | null;
  embedMatch: ReturnType<typeof useMatch> | null;
  sendMessageToEmbed: (type: string, payload?: Record<string, unknown>) => boolean;
  displayAppName: string | null;
  routeConfig: (typeof ROUTE_CONFIG)[string] | undefined;
}

/** Sends a postMessage to the embedded iframe if it supports the protocol. */
const useSendMessageToEmbed = (embedReadyRef: React.MutableRefObject<boolean>) => {
  return useCallback(
    (type: string, payload?: Record<string, unknown>): boolean => {
      if (!embedReadyRef.current) return false;
      return postMessageToEmbeddedIframe(type, payload);
    },
    [embedReadyRef]
  );
};

export const useEmbeddedApp = (): EmbeddedAppState => {
  const { parsedApplications } = useAppContext();
  const { isDarkMode } = useTheme();
  const embedMatch = useMatch('/all-apps/:id');
  const { pathname } = useLocation();
  const embedReadyRef = useRef(false);

  // Keep a ref in sync with the context value so the message handler
  // always reads the latest theme without a stale closure.
  const isDarkModeRef = useRef(isDarkMode);
  useEffect(() => {
    isDarkModeRef.current = isDarkMode;
  }, [isDarkMode]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isEmbedReadyMessage(event)) {
        embedReadyRef.current = true;
        // Immediately sync the current theme with the newly-ready embedded app
        postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: isDarkModeRef.current });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Propagate theme changes to an already-loaded embedded iframe
  useEffect(() => {
    if (embedReadyRef.current) {
      postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode });
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (!embedMatch?.params?.id) {
      embedReadyRef.current = false;
    }
  }, [embedMatch?.params?.id]);

  const embeddedApp = useMemo((): EmbeddedAppInfo | null => {
    if (!embedMatch?.params?.id) return null;
    const app = parsedApplications.filter((a) => a.isActive).find((a) => a.id === embedMatch.params.id);
    if (!app || !app.embeddingEnabled) return null;
    return {
      url: app.applicationLink || null,
      name: app.applicationName || null,
      logoConfig: app.logoConfig || null,
      productType: app.toolUI || null
    };
  }, [embedMatch, parsedApplications]);

  const sendMessageToEmbed = useSendMessageToEmbed(embedReadyRef);

  const routeConfig = ROUTE_CONFIG[pathname];
  // Sections whose routes get the embedded-style top-nav treatment (logo
  // swap + arrow + section name beside the FinSuite icon). For kingpin
  // we pin the name to "frame" so all sub-tabs (overview / goals /
  // insights) read the same in the top nav rather than swapping per tab.
  let sectionDisplayName: string | null = null;
  if (routeConfig?.navigationSection === 'org-tools') {
    sectionDisplayName = routeConfig.navigationText?.toLowerCase() ?? null;
  } else if (routeConfig?.navigationSection === 'kingpin') {
    sectionDisplayName = 'frame';
  }
  const displayAppName = (embeddedApp?.productType === 'Prophecy' ? 'prophecy' : embeddedApp?.name?.toLowerCase()) ?? sectionDisplayName ?? null;

  return { embeddedApp, embedMatch, sendMessageToEmbed, displayAppName, routeConfig };
};
